// The one way UI code runs a ticket action (resolve, claim, snooze, …).
//
//   const { run, runMany } = useTicketAction();
//   run(ticket.id, 'resolve', { note }, { subject: ticket.subject });
//
// The click is reflected on screen immediately (every cached copy of the
// ticket changes), the success toast shows at once, and the dispatcher's 1-3 s
// happens in the background. Only a failure interrupts: the ticket goes back
// and the toast turns into an error with a Retry button.
//
// Everything still posts to workflow 11 via `postAction`, so the dispatcher
// remains the single source of action logic (CLAUDE.md §5). The mechanics —
// which caches change, ordering, rollback — live in ticket-actions.ts.

import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TicketAction } from './api';
import { useAuth } from './auth-guard';
import { mapWithConcurrency } from './concurrency';
import {
  makeActionContext,
  nextActionToastId,
  runTicketAction,
  type RunOptions,
} from './run-ticket-action';
import { outbox } from './send-outbox';
import { hasPendingTicketActions, prepareTicketAction } from './ticket-actions';
import { toastBulkActionDone } from './toast';

export type { RunOptions };

/** In-flight requests per bulk run. Workflow 11 runs on the n8n Starter plan,
 *  which auto-deactivates it if flooded (see lib/concurrency.ts). */
const BULK_LANES = 4;

export interface BulkResult {
  total: number;
  /** Tickets the server refused or couldn't be reached for (already put back). */
  failedIds: string[];
}

export function useTicketAction() {
  const qc = useQueryClient();
  const { teamMember } = useAuth();
  const actorSlackId = teamMember?.slack_user_id ?? null;

  const ctx = useMemo(() => makeActionContext(qc, actorSlackId), [qc, actorSlackId]);

  /** Run one action on one ticket. Resolves `true` once the server confirmed,
   *  `false` if it was refused or unreachable (the toast already says so). It
   *  never rejects, so callers can fire-and-forget. */
  const run = useCallback(
    (
      ticketId: string,
      action: TicketAction,
      payload?: Record<string, unknown>,
      opts?: RunOptions,
    ): Promise<boolean> => runTicketAction(qc, ctx, ticketId, action, payload, opts),
    [qc, ctx],
  );

  /** Run one action on many tickets (bulk bar, merge). Every ticket changes
   *  on screen at once; requests go out four at a time. Resolves when all have
   *  been answered, with the ids that failed. */
  const runMany = useCallback(
    async (
      ticketIds: string[],
      action: TicketAction,
      payload?: Record<string, unknown>,
    ): Promise<BulkResult> => {
      if (ticketIds.length === 0) return { total: 0, failedIds: [] };
      const toastId = nextActionToastId();
      const prepared = ticketIds.map((ticketId) =>
        prepareTicketAction(qc, { ticketId, action, payload }, ctx),
      );
      toastBulkActionDone(action, { total: ticketIds.length, failed: 0 }, { id: toastId });

      const results = await mapWithConcurrency(prepared, BULK_LANES, (p) => p.commit());
      const failedIds = ticketIds.filter((_, i) => results[i]?.status === 'rejected');
      if (failedIds.length > 0) {
        // Replaces the success toast in place with the warning.
        toastBulkActionDone(
          action,
          { total: ticketIds.length, failed: failedIds.length },
          { id: toastId },
        );
      }
      return { total: ticketIds.length, failedIds };
    },
    [qc, ctx],
  );

  return { run, runMany };
}

/**
 * Mount once (App). While an action is still waiting for the server, closing or
 * reloading the tab asks for confirmation — otherwise a request that hasn't
 * been sent yet (queued behind another, or in a bulk run) would be lost with
 * the change already shown as done.
 *
 * The same prompt covers the send outbox: a reply that is queued, sending,
 * failed or put on hold lives only in this tab's memory.
 */
export function usePendingTicketActionsGuard() {
  const qc = useQueryClient();
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasPendingTicketActions(qc) && !outbox.hasUnsentWork()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [qc]);
}
